import { API_BASE } from "../utils/api";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";

const CATEGORIES = [
  { value: "tax_return", label: "Tax Return" },
  { value: "invoice", label: "Invoice" },
  { value: "payslip", label: "Payslip" },
  { value: "bank_statement", label: "Bank Statement" },
  { value: "expense", label: "Expense Document" },
  { value: "contract", label: "Contract" },
  { value: "other", label: "Other" },
];

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx", ".xls", ".xlsx"];
const TAX_YEARS = ["2025/2026", "2024/2025", "2023/2024", "2022/2023", "2021/2022"];

function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function getFileIcon(fileType) {
  if (fileType.includes("pdf")) return "📄";
  if (fileType.includes("image")) return "🖼️";
  if (fileType.includes("word") || fileType.includes("document")) return "📝";
  if (fileType.includes("excel") || fileType.includes("sheet")) return "📊";
  return "📎";
}

export default function UploadDocument() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [category, setCategory] = useState("other");
  const [description, setDescription] = useState("");
  const [taxYear, setTaxYear] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const validateFile = (file) => {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type not allowed. Please upload: ${ALLOWED_EXTENSIONS.join(", ")}`;
    }
    if (file.size > 10 * 1024 * 1024) {
      return `File too large (${formatFileSize(file.size)}). Max 10MB.`;
    }
    if (file.size === 0) return "File appears to be empty.";
    return null;
  };

  const handleFileSelect = (file) => {
    setError("");
    const validationError = validateFile(file);
    if (validationError) { setError(validationError); return; }
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) { setError("Please select a file to upload."); return; }
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("category", category);
      if (description) formData.append("description", description);
      if (taxYear) formData.append("tax_year", taxYear);

      const res = await fetch(`${API_BASE}/api/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed. Please try again.");
      addToast("Document uploaded successfully!", "success");
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      addToast(err.message, "error");
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const inputClass = "w-full border border-luxury-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/40 focus:border-luxury-gold/60 bg-luxury-elevated text-luxury-text placeholder-luxury-muted transition";

  const dropZoneClass = `border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 ${
    isDragging
      ? "border-luxury-gold bg-luxury-gold/5"
      : selectedFile
      ? "border-luxury-cta bg-luxury-cta/5"
      : "border-luxury-border hover:border-luxury-gold/50 hover:bg-luxury-elevated/50"
  }`;

  return (
    <div className="min-h-screen p-6 lg:p-8 bg-luxury-bg">
      <div className="max-w-2xl mx-auto">

        {/* Page header */}
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-luxury-text">Upload Document</h1>
          <p className="text-sm mt-1 text-luxury-secondary">Securely store your tax documents in one place — max 10MB</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Drop Zone Card */}
          <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-8 mb-5">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={dropZoneClass}
            >
              <input ref={fileInputRef} type="file" className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                onChange={handleInputChange} />

              {selectedFile ? (
                <div className="flex flex-col items-center gap-3">
                  <span className="text-5xl">{getFileIcon(selectedFile.type)}</span>
                  <div>
                    <p className="font-semibold text-lg text-luxury-text">{selectedFile.name}</p>
                    <p className="text-sm mt-1 text-luxury-secondary">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setError(""); }}
                    className="text-sm font-medium transition text-red-400 hover:text-red-300">
                    ✕ Remove file
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-luxury-elevated text-luxury-gold">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-base text-luxury-text">
                      {isDragging ? "Drop your file here" : "Drag & drop your file here"}
                    </p>
                    <p className="text-sm mt-1 text-luxury-secondary">
                      or <span className="text-luxury-gold font-medium">browse to upload</span>
                    </p>
                  </div>
                  <p className="text-xs text-luxury-muted">
                    PDF, JPG, PNG, DOC, DOCX, XLS, XLSX · Max 10MB
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 border border-red-800/50 rounded-xl text-sm flex items-center gap-2 bg-red-900/20 text-red-400">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}
          </div>

          {/* Form fields card */}
          <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-8 mb-5">
            <h3 className="text-base font-bold mb-5 text-luxury-text">Document Details</h3>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">
                  Document Category <span className="text-red-400">*</span>
                </label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className={inputClass}>
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Tax Year</label>
                <select value={taxYear} onChange={(e) => setTaxYear(e.target.value)}
                  className={inputClass}>
                  <option value="">Select tax year (optional)</option>
                  {TAX_YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this document (optional)..."
                  rows={3} className={`${inputClass} resize-none`} />
              </div>
            </div>
          </div>

          {/* Submit */}
          <button type="submit" disabled={uploading || !selectedFile}
            className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200
              ${uploading || !selectedFile
                ? "bg-luxury-elevated text-luxury-muted cursor-not-allowed border border-luxury-border"
                : "bg-luxury-cta hover:bg-luxury-cta-hover text-white active:scale-95"
              }`}>
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Uploading...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload Document
              </span>
            )}
          </button>

          <p className="text-center text-xs mt-4 text-luxury-muted">
            🔒 Your documents are encrypted and stored securely
          </p>
        </form>

      </div>
    </div>
  );
}
